/**
 * Environment-aware debug logging for OAuth flows.
 *
 * - oauthDebug: only logs in development (import.meta.env.DEV)
 * - oauthWarn: always logs, but sanitizes sensitive data
 */

const PREFIX = '[OAuth]';

/**
 * Mask strings that might be tokens, secrets, or credentials.
 * Any string longer than 8 characters gets truncated to first 4 chars + "...".
 */
function sanitizeValue(value: unknown): unknown {
  if (typeof value === 'string' && value.length > 8) {
    return value.slice(0, 4) + '...';
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeValue);
  }
  if (value !== null && typeof value === 'object') {
    return sanitizeObject(value as Record<string, unknown>);
  }
  return value;
}

/**
 * Recursively sanitize all string values in an object.
 */
function sanitizeObject(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    result[key] = sanitizeValue(obj[key]);
  }
  return result;
}

/**
 * Sanitize a list of arbitrary arguments for safe logging.
 */
function sanitizeArgs(args: unknown[]): unknown[] {
  return args.map(sanitizeValue);
}

/**
 * Debug log -- only outputs in development builds.
 * Strips sensitive data even in dev so developers build good habits.
 */
export function oauthDebug(message: string, ...data: unknown[]): void {
  if (!import.meta.env.DEV) return;

  if (data.length > 0) {
    console.log(`${PREFIX} ${message}`, ...sanitizeArgs(data));
  } else {
    console.log(`${PREFIX} ${message}`);
  }
}

/**
 * Warning log -- always outputs (all environments) with sanitized data.
 * Use for security warnings, errors, and issues that ops needs to see.
 */
export function oauthWarn(message: string, ...data: unknown[]): void {
  if (data.length > 0) {
    console.warn(`${PREFIX} ${message}`, ...sanitizeArgs(data));
  } else {
    console.warn(`${PREFIX} ${message}`);
  }
}
